import { AxiosResponse } from "axios";

import { oc } from "ts-optchain";
import { DealernetApi } from "./DealernetApi";

import {
  IPagedList,
  ISelectedWarehouseShippingOptionsDto,
  IUpdateWarehouseShippingOptionsDto,
  IWarehouseThirdPartyAccountDto,
} from "@/types";

export const WarehouseShippingOptionsControllerPath = "WarehouseShippingOptions";

export class WarehouseShippingOptionsService {
  /**
   * Gets Warehouse shipping options
   *
   * @static
   * @param {number} accountId
   * @param {number} userId
   * @param {*} siteId
   * @param {string} warehouseCode Use two letter Warehouse Code to get specific results, or send in an empty string for all Warehouses
   * @param {boolean} showAll
   * @returns
   * @memberof WarehouseShippingOptionsService
   */
  public static async LoadWarehouseShippingOptions(
    accountId: number,
    userId: number,
    siteId: any,
    warehouseCode: string,
    showAll: boolean,
    canShipAir: boolean
  ): Promise<ISelectedWarehouseShippingOptionsDto[]> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${WarehouseShippingOptionsControllerPath}/GetWarehouseShippingOptionsByWarehouse`,
      params: {
        accountId,
        userId,
        siteId,
        warehouseCode,
        showAll,
        canShipAir
      },
    });
    
    return oc(response as AxiosResponse<ISelectedWarehouseShippingOptionsDto[]>).data([]);
  }

  public static async LoadFreightTermsList(): Promise<string[]> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${WarehouseShippingOptionsControllerPath}/LoadFreightTerms`,
    });

    return oc(response as AxiosResponse<string[]>).data([]);
  }

  public static async LoadFreightTermsDefault(accountId: number, userId: number): Promise<string> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${WarehouseShippingOptionsControllerPath}/LoadFreightTermDefault`,
      params: {
        accountId,
        userId,
      },
    });

    return oc(response as AxiosResponse<string>).data("");
  }

  public static async LoadThirdPartyDetails(
    accountId: number,
    userId: number
  ): Promise<IPagedList<IWarehouseThirdPartyAccountDto>> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${WarehouseShippingOptionsControllerPath}/LoadThirdPartyAccounts`,
      params: {
        accountId,
        userId,
      },
    });

    return oc(response as AxiosResponse<IPagedList<IWarehouseThirdPartyAccountDto>>).data()!;
  }

  public static async LoadFacingWarehouse(accountId: number, userId: number): Promise<string[]> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${WarehouseShippingOptionsControllerPath}/LoadFacingWarehouse`,
      params: {
        accountId,
        userId,
      },
    });

    return oc(response as AxiosResponse<string[]>).data([]);
  }

  public static async BulkUpdateWarehouseShippingOptionsSelections(
    dealerCodes: string[],
    updatedWarehouseShippingOptions: IUpdateWarehouseShippingOptionsDto
  ): Promise<boolean> {

    var data = {
      ShippingOptionsDto: updatedWarehouseShippingOptions,
      DealerCodes: dealerCodes
    }

    const response = await DealernetApi.client().request({
      method: "post",
      url: `${WarehouseShippingOptionsControllerPath}/BulkUpdateWarehouseShippingOptionsSelections`,
      data: data,
    });

    return oc(response as AxiosResponse<boolean>).data(true);
  }


  public static async BulkUpdateDefaultFreightTerms(
    dealerCodes: string[],
    defaultFreightTerm: string
  ): Promise<boolean> {

    var data = {
      DefaultFreightTerm: defaultFreightTerm,
      DealerCodes: dealerCodes
    }

    const response = await DealernetApi.client().request({
      method: "post",
      url: `${WarehouseShippingOptionsControllerPath}/BulkUpdateDefaultFreightTerms`,
      data: data,
    });

    return oc(response as AxiosResponse<boolean>).data(true);
  }

  public static async UpdateWarehouseShippingOptionsSelections(
    accountId: number,
    userId: number,
    updatedWarehouseShippingOptions: IUpdateWarehouseShippingOptionsDto
  ): Promise<boolean> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${WarehouseShippingOptionsControllerPath}/UpdateWarehouseShippingOptionsSelections`,
      params: {
        accountId,
        userId,
      },
      data: updatedWarehouseShippingOptions,
    });

    return oc(response as AxiosResponse<boolean>).data(true);
  }

  public static async SaveFreightTermDefault(
    accountId: number,
    userId: number,
    updatedFreightTerm: string
  ): Promise<boolean> {
      const response = await DealernetApi.client().request({
        method: "post",
        url: `${WarehouseShippingOptionsControllerPath}/SaveFreightTermDefault`,
        params: {
          accountId,
          userId,
          updatedFreightTerm
        },
      });

      return oc(response as AxiosResponse<boolean>).data(true);
  }
}
